import React, { useCallback, useState } from 'react'

import { useReactFlow } from '@xyflow/react'
import { cloneDeep } from 'lodash'

import Box from 'components_new/atoms/Box'
import Icon from 'components_new/atoms/Icon'

import Node, { InputType } from 'components_new/molecules/Node'

import NodeType from 'types/transformation/enums/NodeType'
import { ClearNode, Node as TNode } from 'types/transformation/types/nodes'

import { BOARD } from 'components_new/molecules/Node/accents'
import { pruneRef, useInputSchema, useIsInvalid, usePruneRefs } from '../hooks'

interface ClearNodeProps {
  id: string
  data: ClearNode
  selected: boolean
}

const VISIBLE_CAP = 5

const Clear = (props: ClearNodeProps) => {
  const { id, data, selected } = props
  const { setNodes } = useReactFlow<TNode>()
  const [showAll, setShowAll] = useState(false)

  const isInvalid = useIsInvalid(data)
  const inputSchema = useInputSchema(data.left_node)

  // input schema has changed, validate references
  usePruneRefs<ClearNode>(
    id,
    (nodeData) => ({
      ...nodeData,
      columns: nodeData.columns.filter((col) => pruneRef(col, inputSchema))
    }),
    [inputSchema]
  )

  const handleToggle = useCallback(
    (columnId: string) => {
      setNodes((prevNodes) =>
        prevNodes.map((node) => {
          if (node.id !== id) {
            return node
          }

          const newNodeData = cloneDeep(node.data) as ClearNode

          newNodeData.columns = newNodeData.columns.includes(columnId)
            ? newNodeData.columns.filter((col) => col !== columnId)
            : [...newNodeData.columns, columnId]

          return {
            ...node,
            data: newNodeData
          }
        })
      )
    },
    [setNodes, id]
  )

  const rows = showAll ? inputSchema : inputSchema.slice(0, VISIBLE_CAP)
  const hidden = inputSchema.length - VISIBLE_CAP

  return (
    <Node
      error={isInvalid}
      icon="RuleOutlined"
      inputType={InputType.SINGLE}
      title={'Clear'}
      selected={selected}
      nodeNumber={data.index}
      type={NodeType.CLEAR}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.25,
          width: '240px'
        }}
      >
        {rows.map((column) => {
          const checked = data.columns.includes(column.id)

          return (
            <Box
              key={column.id}
              className="nodrag ignore-locked-mode"
              role="checkbox"
              onClick={() => handleToggle(column.id)}
              sx={{
                alignItems: 'center',
                borderRadius: '9px',
                cursor: 'pointer',
                display: 'flex',
                gap: 1,
                p: '2px',
                '&:hover .clear-pill': { backgroundColor: '#F0F0F2' }
              }}
            >
              <Box
                sx={{
                  alignItems: 'center',
                  backgroundColor: checked ? BOARD.textPrimary : 'common.white',
                  border: checked ? 'none' : '1.5px solid #D4D4D8',
                  borderRadius: '5px',
                  color: 'common.white',
                  display: 'flex',
                  flex: 'none',
                  height: 18,
                  justifyContent: 'center',
                  width: 18
                }}
              >
                {checked ? (
                  <Icon name="Check" sx={{ fontSize: '13px' }} />
                ) : null}
              </Box>
              <Box
                className="clear-pill"
                sx={{
                  alignItems: 'center',
                  backgroundColor: BOARD.rowBackground,
                  borderRadius: '9px',
                  display: 'flex',
                  gap: 1,
                  justifyContent: 'space-between',
                  minWidth: 0,
                  p: '7px 9px',
                  width: '100%'
                }}
              >
                <Box
                  sx={{
                    color: BOARD.textPrimary,
                    fontFamily: BOARD.fontBody,
                    fontSize: '12.5px',
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {column.name}
                </Box>
                {column.reference_node_index ? (
                  <Box
                    component="span"
                    sx={{
                      alignItems: 'center',
                      backgroundColor: BOARD.signalGreen,
                      borderRadius: '4px',
                      color: 'common.white',
                      display: 'inline-grid',
                      flex: 'none',
                      fontFamily: BOARD.fontBody,
                      fontSize: '9px',
                      fontWeight: 700,
                      height: '15px',
                      minWidth: '15px',
                      placeItems: 'center',
                      px: '3px'
                    }}
                  >
                    {column.reference_node_index}
                  </Box>
                ) : null}
              </Box>
            </Box>
          )
        })}

        {hidden > 0 ? (
          <Box
            role="button"
            onClick={() => setShowAll((prev) => !prev)}
            sx={{
              alignItems: 'center',
              borderRadius: '9px',
              color: BOARD.textSecondary,
              cursor: 'pointer',
              display: 'flex',
              fontFamily: BOARD.fontDisplay,
              fontSize: '12px',
              fontWeight: 600,
              gap: 0.5,
              justifyContent: 'center',
              mt: 0.5,
              p: '6px',
              '&:hover': { backgroundColor: BOARD.chipBackground }
            }}
          >
            {showAll ? 'Visa färre' : `Visa alla (${hidden} till)`}
            <Icon
              name={showAll ? 'ExpandLess' : 'ExpandMore'}
              sx={{ fontSize: '16px' }}
            />
          </Box>
        ) : null}
      </Box>
    </Node>
  )
}

export default Clear
